import { extractMainImpactScore } from '@src/utility/impactScore';
import { useMemo, useState } from 'react';

import type { ShareableImageType } from '../../../types.ts/story';

export type PullQuoteSortMode = 'impact_score' | 'most_recent';

export type QuoteLimit = 6 | 9 | 12 | 15 | 'all';

export const QUOTE_LIMIT_OPTIONS: { value: QuoteLimit; label: string }[] = [
  { value: 6, label: 'Top 6' },
  { value: 9, label: 'Top 9' },
  { value: 12, label: 'Top 12' },
  { value: 15, label: 'Top 15' },
  { value: 'all', label: 'Show All' },
];

/**
 * Hook for sorting and limiting pull quote results in the selection modal.
 * - Default sort is by impact score (highest first)
 * - Default limit is Top 6
 */
export function usePullQuoteSelection(quotes: ShareableImageType[]) {
  const [sortMode, setSortMode] = useState<PullQuoteSortMode>('impact_score');
  const [quoteLimit, setQuoteLimit] = useState<QuoteLimit>('all');

  const sortedQuotes = useMemo(() => {
    if (!quotes || quotes.length === 0) return quotes;

    if (sortMode === 'most_recent') {
      if (quoteLimit === 'all') return quotes;
      return quotes.slice(0, quoteLimit);
    }

    const sorted = [...quotes].sort((a, b) => {
      const scoreA = extractMainImpactScore(a.metadata);
      const scoreB = extractMainImpactScore(b.metadata);
      return scoreB - scoreA;
    });

    if (quoteLimit === 'all') return sorted;
    return sorted.slice(0, quoteLimit);
  }, [quotes, sortMode, quoteLimit]);

  const totalQuotes = quotes?.length ?? 0;

  return {
    sortedQuotes,
    sortMode,
    setSortMode,
    quoteLimit,
    setQuoteLimit,
    totalQuotes,
  };
}
